import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import type { ApiOperationItems } from "~enums/apiOperationItems";
import { RoleStatuses } from "~enums/roleStatuses.ts";
import { Roles } from "~enums/roles.ts";
import { useAddRole } from "~hooks/apiHooks/useAddRole.ts";
import { useDeleteRole } from "~hooks/apiHooks/useDeleteRole.ts";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { RosterDataTableRow } from "~pages/Roster/types/rosterDataTableRow.ts";

type Props = {
  entityId?: string;
  seasonId?: string;
  fixtureId?: string;
  onSuccess: () => void | Promise<void>;
  successMessageId: string;
  operationItem: ApiOperationItems;
};

type CaptainRow = RosterDataTableRow & DataTableRow;

export const useRosterCaptainToggle = ({ entityId, seasonId, fixtureId, onSuccess, successMessageId, operationItem }: Props) => {
  const { mutateAsync: createCaptainRole } = useAddRole();
  const { mutateAsync: deleteCaptainRole } = useDeleteRole();
  const { execute } = useApiOperations(operationItem);

  // The roles endpoints may answer with an empty body, so a resolved request means success
  const runCaptainMutation = async (request: () => Promise<unknown>) => {
    const isSucceeded = await execute(
      async () => {
        await request();
        return true;
      },
      { successMessageId },
    );

    if (isSucceeded) {
      await onSuccess();
    }
  };

  const handleCaptainToggle = async (row: CaptainRow, isCaptain: boolean) => {
    row.disabled = true;

    try {
      const captainRoleId = row.captainRoleId;
      if (!isCaptain && captainRoleId) {
        await runCaptainMutation(() => deleteCaptainRole(captainRoleId));
      } else if (isCaptain && !row.captainRoleId && entityId && seasonId) {
        await runCaptainMutation(() =>
          createCaptainRole({
            personId: row.personId,
            entityId,
            seasonId,
            role: Roles.Captain,
            fixtureId,
            status: RoleStatuses.Active,
          }),
        );
      }
    } finally {
      row.disabled = false;
    }
  };

  return {
    handleCaptainToggle,
    deleteCaptainRole: (roleId: string) => runCaptainMutation(() => deleteCaptainRole(roleId)),
  };
};
